import { useCallback, useEffect, useState } from "react";
import { IGif, INiche } from "@/common/services/types";
import { apiMain, apiNiches } from "@/common/services";
import { Roles } from "@/common/buses/popups/popup-types";
import { useGetMyNiches } from "@/common/queries/queries";

interface IProps {
  gifId: string;
  role: Roles;
}

interface IReturn {
  gif?: IGif;
  niches: INiche[];
  isLoading: boolean;
  isError: boolean;
  hasMore: boolean;
  handleFetchMore: () => void;
}

const PAGE_SIZE = 100;

export const useData = ({ gifId, role }: IProps): IReturn => {
  const [gif, setGif] = useState<IGif>();
  const [niches, setNiches] = useState<INiche[]>([]);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const { data: myNichesData } = useGetMyNiches({ previews: true });

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const response = await apiMain.fetchGifById(gifId);
        setGif(response.gif);

        if (role === Roles.owner) {
          setNiches(response.niches);
        }

        if (role === Roles.admin) {
          const nicheResponse = await apiNiches.getNiches({
            page,
            count: PAGE_SIZE,
            order: "name",
            previews: false,
            selected: response.gif.niches || [],
          });
          setNiches(prev => [...prev, ...nicheResponse.niches]);
          setHasMore(nicheResponse.total > page * PAGE_SIZE);
        }

        if (role === Roles.moderator) {
          setNiches(myNichesData?.niches || []);
        }
      } catch (e) {
        console.error(`Could not get gif niches or niches list: ${e}`);
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [gifId, role, myNichesData, page]);

  const handleFetchMore = useCallback(() => {
    setPage(prev => prev + 1);
  }, []);

  return {
    gif,
    niches,
    isLoading,
    isError,
    hasMore,
    handleFetchMore,
  };
};
